import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assignmentAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiUpload, FiX } from 'react-icons/fi';

const AssignmentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    class_id: '',
    batch_id: '',
    subject_id: '',
    due_date: '',
    total_marks: '',
    passing_marks: '',
    status: 'draft',
    allow_late_submission: false
  });

  useEffect(() => {
    fetchInitialData();
    if (isEdit) {
      fetchAssignment();
    }
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [classesRes, subjectsRes] = await Promise.all([
        masterAPI.getClasses(),
        masterAPI.getSubjects()
      ]);

      if (classesRes.data.success) setClasses(classesRes.data.data);
      if (subjectsRes.data.success) setSubjects(subjectsRes.data.data);
    } catch (error) {
      toast.error('Error loading data');
      console.error(error);
    }
  };

  const fetchBatches = async (classId) => {
    try {
      const response = await masterAPI.getBatches({ class_id: classId });
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      const response = await assignmentAPI.getAssignment(id);
      if (response.data.success) {
        const assignment = response.data.data;
        setFormData({
          title: assignment.title,
          description: assignment.description || '',
          class_id: assignment.class_id,
          batch_id: assignment.batch_id || '',
          subject_id: assignment.subject_id || '',
          due_date: assignment.due_date,
          total_marks: assignment.total_marks,
          passing_marks: assignment.passing_marks || '',
          status: assignment.status,
          allow_late_submission: assignment.allow_late_submission === 1
        });
        if (assignment.class_id) {
          fetchBatches(assignment.class_id);
        }
      }
    } catch (error) {
      toast.error('Error loading assignment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId) => {
    setFormData({ ...formData, class_id: classId, batch_id: '' });
    setBatches([]);
    if (classId) {
      fetchBatches(classId);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedFile) {
        formDataToSend.append('attachment', selectedFile);
      }

      let response;
      if (isEdit) {
        response = await assignmentAPI.updateAssignment(id, formDataToSend);
      } else {
        response = await assignmentAPI.createAssignment(formDataToSend);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/assignments');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/assignments')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back to Assignments
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {isEdit ? 'Edit Assignment' : 'Create New Assignment'}
        </h1>
        <p className="text-sm md:text-base text-gray-600 mt-1">
          {isEdit ? 'Update assignment details' : 'Fill in the details to create a new assignment'}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              placeholder="e.g., Math Homework - Chapter 5"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              rows="4"
              placeholder="Provide detailed instructions for the assignment..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
              <select
                value={formData.class_id}
                onChange={(e) => handleClassChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                required
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.class}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Batch (Optional)</label>
              <select
                value={formData.batch_id}
                onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                disabled={!formData.class_id}
              >
                <option value="">All Batches</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.batch}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject (Optional)</label>
              <select
                value={formData.subject_id}
                onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Due Date *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                required
              />
            </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total Marks *</label>
            <input
              type="number"
              min="1"
              value={formData.total_marks}
              onChange={(e) => setFormData({ ...formData, total_marks: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              placeholder="e.g., 100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Passing Marks</label>
            <input
              type="number"
              min="0"
              value={formData.passing_marks}
              onChange={(e) => setFormData({ ...formData, passing_marks: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
              placeholder="e.g., 40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600"
                required
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allow_late_submission}
                onChange={(e) => setFormData({ ...formData, allow_late_submission: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-600"
              />
              <span className="text-sm font-medium text-gray-700">Allow late submissions</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Attachment (Optional)</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              {selectedFile ? (
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded">
                  <span className="text-sm text-gray-700">{selectedFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              ) : (
                <div>
                  <FiUpload className="mx-auto text-gray-400 mb-2" size={32} />
                  <label className="cursor-pointer">
                    <span className="text-primary-600 hover:text-primary-700 font-medium">
                      Click to upload
                    </span>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300"
            >
              {submitting ? 'Saving...' : (isEdit ? 'Update Assignment' : 'Create Assignment')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/assignments')}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignmentForm;
